<template>
    <b-modal
        visible
        title="Цвет и название в плане"
        hide-footer
        scrollable
        no-close-on-backdrop
        :no-close-on-esc="saving"
        :hide-header-close="saving"
        @hide="preventHideWhileSaving"
        @shown="focusEditor"
        @hidden="$emit('close')"
    >
        <p class="text-muted small">
            Цвет и краткое название отображаются в плане у всех пользователей.
        </p>
        <b-form-group label="Объект или группа" label-for="planning-name-target">
            <v-select
                input-id="planning-name-target"
                :value="selectedKey"
                :options="targets"
                :reduce="target => target.key"
                label="label"
                :clearable="false"
                :disabled="saving"
                placeholder="Найти по названию или сокращению"
                append-to-body
                :calculate-position="positionDropdown"
                @input="selectTarget"
            >
                <template #option="target">
                    <div class="planning-name-option">
                        <small>{{ targetTypeLabel(target) }}</small>
                        <strong>{{ target.planning_code || 'Без краткого названия' }}</strong>
                        <span>{{ target.name }}</span>
                    </div>
                </template>
                <template #no-options>Объекты и группы не найдены.</template>
            </v-select>
        </b-form-group>
        <form v-if="selectedTarget" @submit.prevent="submit">
            <div class="planning-name-target">
                <small>{{ targetTypeLabel(selectedTarget) }}</small>
                <strong>{{ selectedTarget.name }}</strong>
                <span class="planning-target-preview" :style="previewStyle">
                    {{ normalizedCode || 'Без краткого названия' }}
                </span>
            </div>
            <b-form-group label="Краткое название для плана" label-for="planning-name-code">
                <b-form-input
                    id="planning-name-code"
                    ref="codeInput"
                    v-model="draft"
                    maxlength="16"
                    autocomplete="off"
                    :disabled="saving || !canEditName"
                    :state="draft && codeError ? false : null"
                    placeholder="Например, ВНК1"
                    @input="resetConflict"
                ></b-form-input>
                <small v-if="canEditName" class="d-block mt-1" :class="codeError && draft ? 'text-danger' : 'text-muted'">
                    {{ draft && codeError || 'До 16 символов: буквы, цифры, дефис, / и _. Буквы сохраняются заглавными.' }}
                </small>
            </b-form-group>
            <div v-if="conflictOwner" class="planning-name-conflict">
                <strong>Название «{{ normalizedCode }}» уже занято</strong>
                <p>{{ ownerLabel }}: {{ conflictOwner.name }}</p>
                <b-form-group
                    label="Новое краткое название для прежнего владельца"
                    label-for="planning-name-replacement"
                >
                    <b-form-input
                        id="planning-name-replacement"
                        v-model="replacement"
                        maxlength="16"
                        autocomplete="off"
                        :disabled="saving"
                        :state="replacement && replacementError ? false : null"
                        placeholder="Введите другое свободное название"
                        @input="errorMessage = ''"
                    ></b-form-input>
                    <small v-if="replacement && replacementError" class="text-danger">
                        {{ replacementError }}
                    </small>
                </b-form-group>
                <div class="planning-name-transfer">
                    <span>{{ conflictOwner.name }}</span>
                    <strong>{{ normalizedCode }} → {{ normalizedReplacement || '…' }}</strong>
                    <span>{{ selectedTarget.name }}</span>
                    <strong>{{ originalCode || 'Без названия' }} → {{ normalizedCode }}</strong>
                </div>
                <small class="d-block mt-2">Оба названия изменятся одновременно.</small>
            </div>
            <fieldset v-if="canEditColor" class="planning-target-colors" :disabled="saving">
                <legend>Цвет в плане</legend>
                <button
                    type="button"
                    class="planning-target-auto"
                    :class="{'is-active': draftColor === null}"
                    :aria-pressed="draftColor === null ? 'true' : 'false'"
                    @click="draftColor = null"
                >
                    <span :style="{backgroundColor: selectedTarget.automatic_color}"></span>
                    Назначить автоматически
                    <i v-if="draftColor === null" class="fas fa-check ml-auto" aria-hidden="true"></i>
                </button>
                <div v-for="group in colorGroups" :key="group.start" class="planning-target-color-group">
                    <div class="planning-target-color-heading">{{ group.label }}</div>
                    <div class="planning-target-palette" role="group" :aria-label="group.label">
                        <button
                            v-for="(color, index) in group.colors"
                            :key="color"
                            type="button"
                            :class="{'is-active': draftColor === color}"
                            :style="{backgroundColor: color, color: colorText(color)}"
                            :title="colorLabel(group.start + index)"
                            :aria-label="colorLabel(group.start + index)"
                            :aria-pressed="draftColor === color ? 'true' : 'false'"
                            @click="draftColor = color"
                        >
                            <i v-if="draftColor === color" class="fas fa-check" aria-hidden="true"></i>
                            <span v-else aria-hidden="true">Аа</span>
                        </button>
                    </div>
                </div>
                <button
                    v-if="originalColor && !colorPalette.includes(originalColor)"
                    type="button"
                    class="planning-target-auto mt-2"
                    :class="{'is-active': draftColor === originalColor}"
                    :aria-pressed="draftColor === originalColor ? 'true' : 'false'"
                    @click="draftColor = originalColor"
                >
                    <span :style="{backgroundColor: originalColor}"></span>
                    Закреплённый цвет из прежней палитры
                    <i v-if="draftColor === originalColor" class="fas fa-check ml-auto" aria-hidden="true"></i>
                </button>
                <small class="d-block text-muted mt-2">
                    Серый закреплён за отдыхом, жёлтый — за выбытием из планирования (лупой).
                    Автоматические цвета подбираются по переходам между объектами; ручной выбор сохраняется.
                </small>
            </fieldset>
            <p class="small text-muted">
                Изменения появятся во всех неделях плана, включая прошлые.
                Назначения сотрудников сохранятся.
            </p>
            <b-alert v-if="errorMessage" show variant="danger" class="py-2">
                {{ errorMessage }}
            </b-alert>
            <div class="d-flex justify-content-end align-items-center">
                <b-button variant="outline-secondary" :disabled="saving" @click="$emit('close')">
                    Отмена
                </b-button>
                <b-button
                    type="submit"
                    class="ml-2"
                    :variant="conflictOwner ? 'warning' : 'primary'"
                    :disabled="!canSave"
                >
                    <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                    {{ conflictOwner ? 'Переназначить название' : 'Сохранить' }}
                </b-button>
            </div>
        </form>
        <p v-else class="text-muted small mb-0">
            Выберите объект или группу, чтобы задать или изменить краткое название.
        </p>
    </b-modal>
</template>

<script>
import {positionVueSelectDropdown} from '@/utils/vueSelectPositioning';

function normalized(value) {
    return String(value || '').trim().toLocaleUpperCase('ru-RU');
}

function validateCode(value) {
    if (!value) return 'Введите краткое название.';
    if (value.length > 16) return 'Не больше 16 символов.';
    return /^[0-9A-ZА-ЯЁ/_-]+$/u.test(value)
        ? ''
        : 'Допустимы буквы, цифры, дефис, / и _. Без пробелов.';
}

export default {
    name: 'WorkforcePlanningTargetModal',
    props: {
        targets: {type: Array, default: () => []},
        initialTargetKey: {type: String, default: null},
        saveName: {type: Function, required: true},
        saveColor: {type: Function, required: true},
        colorPalette: {type: Array, default: () => []},
        colorText: {type: Function, default: () => '#30485d'}
    },
    data() {
        return {
            selectedKey: null,
            draft: '',
            originalCode: null,
            draftColor: null,
            originalColor: null,
            conflictOwner: null,
            replacement: '',
            errorMessage: '',
            saving: false
        };
    },
    computed: {
        colorGroups() {
            const labels = ['Светлые', 'Насыщенные', 'Тёмные'];
            return Array.from({length: Math.ceil(this.colorPalette.length / 12)}, (_, index) => ({
                label: labels[index] || 'Другие оттенки', start: index * 12,
                colors: this.colorPalette.slice(index * 12, (index + 1) * 12)
            }));
        },
        selectedTarget() {
            return this.targets.find(target => target.key === this.selectedKey) || null;
        },
        canEditName() { return Boolean(this.selectedTarget && this.selectedTarget.can_edit_name); },
        canEditColor() { return Boolean(this.selectedTarget && this.selectedTarget.can_edit_color); },
        nameChanged() { return this.canEditName && this.normalizedCode !== normalized(this.originalCode); },
        colorChanged() { return this.canEditColor && this.draftColor !== this.originalColor; },
        previewStyle() {
            const color = this.canEditColor ? this.draftColor : this.originalColor;
            const background = color || this.selectedTarget && this.selectedTarget.automatic_color;
            return {backgroundColor: background, color: this.colorText(background)};
        },
        normalizedCode() { return normalized(this.draft); },
        normalizedReplacement() { return normalized(this.replacement); },
        codeError() { return validateCode(this.normalizedCode); },
        replacementError() {
            return this.normalizedReplacement === this.normalizedCode
                ? 'Для прежнего владельца нужно другое название.'
                : validateCode(this.normalizedReplacement);
        },
        ownerLabel() {
            return ({object: 'Объект', object_group: 'Группа объектов', planned_object: 'Плановый объект'})[
                this.conflictOwner && this.conflictOwner.target_type
            ];
        },
        canSave() {
            return Boolean(this.selectedTarget && !this.saving
                && (this.nameChanged || this.colorChanged)
                && (!this.nameChanged || (!this.codeError
                    && (!this.conflictOwner || !this.replacementError))));
        }
    },
    created() {
        if (this.initialTargetKey) this.selectTarget(this.initialTargetKey);
    },
    methods: {
        positionDropdown: positionVueSelectDropdown,
        targetTypeLabel(target) {
            return ({object: 'Объект', object_group: 'Группа объектов', planned_object: 'Плановый объект'})[
                target && target.target_type
            ];
        },
        colorLabel(index) {
            const hue = ['Красный', 'Оранжевый', 'Салатовый', 'Зелёный', 'Изумрудный', 'Мятный',
                'Бирюзовый', 'Синий', 'Индиго', 'Фиолетовый', 'Пурпурный', 'Малиновый'][index % 12];
            const shade = ['светлый', 'насыщенный', 'тёмный'][Math.floor(index / 12)];
            return shade ? `${hue} — ${shade}` : `Цвет ${index + 1}`;
        },
        preventHideWhileSaving(event) {
            if (this.saving) event.preventDefault();
        },
        focusEditor() {
            if (this.$refs.codeInput) this.$refs.codeInput.focus();
        },
        selectTarget(key) {
            this.selectedKey = key;
            this.originalCode = this.selectedTarget && this.selectedTarget.planning_code || null;
            this.originalColor = this.selectedTarget && this.selectedTarget.planning_color_source === 'custom'
                ? this.selectedTarget.planning_color || null : null;
            this.draftColor = this.originalColor;
            this.draft = this.originalCode || '';
            this.resetConflict();
            this.$nextTick(this.focusEditor);
        },
        resetConflict() {
            this.conflictOwner = null;
            this.replacement = '';
            this.errorMessage = '';
        },
        async submit() {
            if (!this.canSave) return;
            this.saving = true;
            this.errorMessage = '';
            let nameSaved = false;
            const input = {
                planning_code: this.normalizedCode,
                expected_planning_code: this.originalCode
            };
            if (this.conflictOwner) {
                input.conflict_owner = {
                    target_type: this.conflictOwner.target_type,
                    target_id: this.conflictOwner.target_id
                };
                input.rename_conflict_to = this.normalizedReplacement;
            }
            try {
                if (this.nameChanged) {
                    const saved = await this.saveName(this.selectedTarget, input);
                    this.originalCode = saved.planning_code;
                    this.draft = saved.planning_code;
                    this.resetConflict();
                    nameSaved = true;
                }
                if (this.colorChanged) {
                    await this.saveColor(this.selectedTarget, this.draftColor);
                    this.originalColor = this.draftColor;
                }
                this.$emit('saved');
                this.$emit('close');
            } catch (error) {
                const data = error && error.response && error.response.data || {};
                const details = data.details || {};
                if (data.code === 'WORKFORCE_PLANNING_CODE_CONFLICT'
                    && details.owner && normalized(details.planning_code) === this.normalizedCode) {
                    this.conflictOwner = details.owner;
                    this.replacement = '';
                } else {
                    if (data.code === 'WORKFORCE_PLANNING_CODE_CONFLICT_CHANGED') this.resetConflict();
                    const message = data.msg || data.message || 'Не удалось сохранить настройки.';
                    this.errorMessage = nameSaved ? `Название сохранено. Цвет не сохранён: ${message}` : message;
                }
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style scoped>
.planning-name-option { display: flex; flex-direction: column; white-space: normal; line-height: 1.35; }
.planning-name-option small { opacity: 0.75; }
.planning-name-target { display: flex; flex-direction: column; margin: 0 0 16px; padding: 12px; border-radius: 8px; background: #f2f5f8; overflow-wrap: anywhere; }
.planning-name-target small { color: #667789; margin-bottom: 4px; }
.planning-target-preview { align-self: flex-start; margin-top: 10px; padding: 5px 10px; border-radius: 5px; font-size: 0.85rem; font-weight: 700; overflow-wrap: anywhere; }
.planning-target-colors { margin: 0 0 16px; }
.planning-target-colors legend { font-size: 1rem; margin-bottom: 8px; }
.planning-target-auto { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 10px; border: 1px solid #cdd7e0; border-radius: 6px; background: #fff; color: #31485e; text-align: left; font-size: 0.85rem; }
.planning-target-auto > span { flex: 0 0 20px; height: 20px; border-radius: 4px; border: 1px solid rgba(0, 0, 0, 0.2); }
.planning-target-auto.is-active { border-color: #55a77d; background: #f0faf5; }
.planning-target-color-group { margin-top: 14px; }
.planning-target-color-heading { margin-bottom: 7px; color: #536879; font-size: 0.8rem; font-weight: 600; }
.planning-target-palette { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; }
.planning-target-palette button { display: flex; align-items: center; justify-content: center; width: 100%; height: 36px; padding: 0; border: 2px solid #fff; border-radius: 6px; box-shadow: 0 0 0 1px #9baab8; font-weight: 700; }
.planning-target-palette button.is-active { box-shadow: 0 0 0 2px #172f46; }
.planning-target-palette button:focus-visible, .planning-target-auto:focus-visible { outline: 2px solid #1976bd; outline-offset: 3px; }
.planning-target-colors:disabled button { cursor: wait; opacity: 0.7; }
.planning-name-conflict { padding: 12px; margin-bottom: 16px; border: 1px solid #e1c56c; border-radius: 8px; background: #fff8e4; overflow-wrap: anywhere; }
.planning-name-conflict p { margin: 6px 0 14px; }
.planning-name-transfer { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px 12px; font-size: 0.85rem; }
@media (max-width: 575px) {
    .planning-name-transfer { grid-template-columns: 1fr; gap: 3px; }
    .planning-name-transfer strong { margin-bottom: 8px; }
}
</style>
